// src/components/crm/FlaggedInboxRow.js
// PHASE 1.6 VERIFIER: r1-16-web-row-16a3f2b8
//
// Per-row component for the Phase 1.6 Flagged Inbox view. Renders a single
// unresolved flagged note assigned to the current user, with three actions:
//
//   - Open    → navigate to the note's most actionable attached entity AND
//                mark any related note_flag notifications as read (mirrors
//                the bell-click behavior in ProtectedCrmLayout.handleNotificationClick
//                so the bell badge stays in sync with the inbox surface)
//   - Resolve → opens ResolveFlagModal (reuses Phase 1.3 component)
//   - Unflag  → opens UnflagModal       (reuses sub-batch 3.1 component)
//
// Permission posture matches NoteCard:
//   - Resolve always available — the inbox query filters to flagTargetUserId,
//     so every visible row has the current user as flag target. canActOnFlag
//     (author OR flagTarget OR high-level) is always satisfied.
//   - Unflag narrower — author OR high-level only. Flag target excluded
//     (their grievance flow is reassign, accessible via Open → NoteCard).
//     Mirrors the server gate in functions/index.js exports.unflagNote.
//
// Entity context line is intentionally type-only ("on a customer", "on a
// job") per the Phase 1.6 design discussion. Reasons:
//   1. Matches the existing bell-notification message format, which also
//      doesn't include the entity name in the message text.
//   2. No prop drilling of org collections required.
//   3. Stays consistent when Phase 1.18 ships richer note attachment UI
//      and the inbox sees mixed entity types — type-only context degrades
//      gracefully across customer/job/quote/invoice rows.
//
// Reassign is intentionally NOT in the row action set. The user can Open
// the note and use NoteCard's Reassign action there. Keeps the inbox row
// focused on triage; richer flag manipulation happens at the note's home
// surface.

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatDistanceToNow } from 'date-fns';
import {
  Flag,
  Phone,
  Wrench,
  AlertTriangle,
  MessageSquare,
  ExternalLink,
  CheckCircle2,
  FlagOff,
} from 'lucide-react';
import { getNoteAttachmentRoute } from '../../constants/notificationRouting';
import ResolveFlagModal from './ResolveFlagModal';
import UnflagModal from './UnflagModal';

// Mirrors NotificationsPanel.js — keeps the visual vocabulary identical
// between the bell panel and the inbox.
const FLAG_TYPE_CONFIG = {
  callback: { icon: Phone,         label: 'Callback Needed',  color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200'   },
  followup: { icon: Wrench,        label: 'Follow-up Job',    color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  issue:    { icon: AlertTriangle, label: 'Issue to Resolve', color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200'    },
  fyi:      { icon: MessageSquare, label: 'General FYI',      color: 'text-slate-600',  bg: 'bg-slate-50',  border: 'border-slate-200'  },
};

const ENTITY_TYPE_LABEL = {
  customer: 'a customer',
  job:      'a job',
  quote:    'a quote',
  invoice:  'an invoice',
};

// Mirrors functions/index.js HIGH_LEVEL_ROLES list. If you change one, change both.
const HIGH_LEVEL_ROLES = [
  'Admin',
  'Manager',
  'Owner/System Admin',
  'Owner / System Admin',
  'Owner',
  'System Admin',
];

// Convert Firestore Timestamp / Date / ISO string to a Date safely.
const toDateSafe = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

// Resolve the row's attached-to type using the same priority order as the
// server (job > quote > invoice > customer). Used for the entity context
// label only — the actual URL is computed by getNoteAttachmentRoute.
const resolveAttachedType = (note) => {
  if (!Array.isArray(note?.attachedTo)) return null;
  const priority = ['job', 'quote', 'invoice', 'customer'];
  for (const t of priority) {
    if (note.attachedTo.some(
      (a) => a && a.type === t && typeof a.id === 'string' && a.id,
    )) {
      return t;
    }
  }
  return null;
};

const PREVIEW_LEN = 140;

const FlaggedInboxRow = ({ note, currentUserProfile, orgUsers, orgId }) => {
  const navigate = useNavigate();
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showUnflagModal, setShowUnflagModal] = useState(false);
  const [opening, setOpening] = useState(false);

  // Narrower permission than the row's visibility itself — author OR
  // high-level only. Flag target is excluded. Mirrors NoteCard.canUnflag
  // and the server-side gate in functions/index.js exports.unflagNote.
  const canUnflag = useMemo(() => {
    if (!note || !currentUserProfile?.id) return false;
    const isAuthor = note.authorId === currentUserProfile.id;
    const isHighLevel = HIGH_LEVEL_ROLES.includes(currentUserProfile.role);
    return isAuthor || isHighLevel;
  }, [note, currentUserProfile]);

  if (!note) return null;

  const flagConfig = note.flagType ? FLAG_TYPE_CONFIG[note.flagType] : null;
  const FlagTypeIcon = flagConfig?.icon || null;
  const addedAtDate = toDateSafe(note.addedAt);
  const timeAgo = addedAtDate
    ? formatDistanceToNow(addedAtDate, { addSuffix: true })
    : '';
  const attachedType = resolveAttachedType(note);
  const entityLabel = attachedType ? ENTITY_TYPE_LABEL[attachedType] : null;

  const rawText = (note.text || '').toString();
  const preview = rawText.length > PREVIEW_LEN
    ? `${rawText.slice(0, PREVIEW_LEN).trimEnd()}…`
    : rawText;

  // Open: navigate to the most-actionable attached entity AND mark related
  // note_flag notifications as read so the bell badge stays in sync. The
  // mark-read is fire-and-forget — failure here doesn't block navigation.
  //
  // Notification query matches resolveNoteFlag's cleanup query
  // (functions/index.js): type='note_flag', entityId=noteId. We keep the
  // isRead==false filter here because the only purpose is to flip unread
  // to read; updating already-read docs would be wasted writes.
  //
  // Known limitation: when a note was flagged with scheduleFollowUp=true,
  // the notification's entityId points at the follow-up Job ID, not the
  // noteId. This query misses those, matching resolveNoteFlag's existing
  // behavior. Cleaning that up is logged as backlog work.
  const handleOpen = async () => {
    if (opening) return;
    setOpening(true);

    const uid = currentUserProfile?.id;
    if (uid && orgId && note.id) {
      try {
        const notifRef = collection(db, 'users', uid, 'notifications');
        const q = query(
          notifRef,
          where('type', '==', 'note_flag'),
          where('entityId', '==', note.id),
          where('isRead', '==', false),
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const batch = writeBatch(db);
          snap.forEach((d) => batch.update(d.ref, { isRead: true }));
          await batch.commit();
        }
      } catch (err) {
        // Non-fatal — log and continue to navigation.
        console.warn(
          'FlaggedInboxRow: mark-read on Open failed (non-fatal):',
          err,
        );
      }
    }

    const path = getNoteAttachmentRoute(note);
    if (path) {
      navigate(path);
    }
    // path === null when attachedTo[] was empty. Open button is disabled
    // in that case, so this branch is theoretically unreachable.
    setOpening(false);
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
        {/* Top row: flag chip + author/time + entity context */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {flagConfig && FlagTypeIcon && (
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${flagConfig.bg} ${flagConfig.border}`}>
              <Flag size={10} className={flagConfig.color} />
              <FlagTypeIcon size={11} className={flagConfig.color} />
              <span className={flagConfig.color}>{flagConfig.label}</span>
            </span>
          )}
          <span className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">
              {note.authorName || 'Unknown'}
            </span>
            {' flagged this '}
            {timeAgo}
            {entityLabel && (
              <>
                {' on '}{entityLabel}
              </>
            )}
          </span>
        </div>

        {/* Note text preview */}
        <p className="text-sm text-slate-700 whitespace-pre-line break-words">
          {preview || <em className="text-slate-400">(no text)</em>}
        </p>

        {/* Action row — mirrors NoteCard: [Unflag] [Open] [Resolve] */}
        <div className="flex items-center justify-end gap-2 mt-3 flex-wrap">
          {canUnflag && (
            <button
              type="button"
              onClick={() => setShowUnflagModal(true)}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
            >
              <FlagOff size={12} /> Unflag
            </button>
          )}
          <button
            type="button"
            onClick={handleOpen}
            disabled={opening || !attachedType}
            title={!attachedType ? 'Nothing to open — this note has no attached entity.' : 'Open the attached entity'}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ExternalLink size={12} /> Open
          </button>
          {/* FO-6: price_review flags have NO inbox Resolve — they can only be
              cleared by opening the job, correcting the price, and clicking
              "Mark as reviewed" on the line item (which calls resolveNoteFlag
              after the actual correction). This prevents dismissing a pricing
              flag without reviewing the tech-entered price, which would let a
              wrongly-priced item reach the invoice. All other flag types keep
              the normal Resolve-with-note action. */}
          {note.flagType !== 'price_review' && (
            <button
              type="button"
              onClick={() => setShowResolveModal(true)}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white bg-amber-500 hover:bg-amber-600 transition-colors"
            >
              <CheckCircle2 size={12} /> Resolve
            </button>
          )}
        </div>
      </div>

      {showResolveModal && (
        <ResolveFlagModal
          note={note}
          orgId={orgId}
          onClose={() => setShowResolveModal(false)}
        />
      )}
      {showUnflagModal && (
        <UnflagModal
          note={note}
          orgId={orgId}
          onClose={() => setShowUnflagModal(false)}
        />
      )}
    </>
  );
};

export default FlaggedInboxRow;